import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useHttpMutation } from '@/api';
import { useQueryClient } from 'react-query';
import { API_URLS } from '../../consts';
import { LoggerService } from '@/shared/logger';
import { getStoreState } from '@/redux';

interface WinCelebrationProps {
  onComplete?: () => void;
}

interface ProgressionData {
  xp: number;
  levelName: string;
  levelEmoji: string;
  currentLevelMinXP: number;
  nextLevelMinXP: number;
}

interface GetCoinsResponse {
  balance: number;
  xpEarned?: number;
  leveledUp?: boolean;
  progression?: ProgressionData;
}

export const WinCelebration: FC<WinCelebrationProps> = ({ onComplete }) => {
  const [coinsCollected, setCoinsCollected] = useState(false);
  const [newBalance, setNewBalance] = useState<number | null>(null);
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [leveledUp, setLeveledUp] = useState(false);
  const [progression, setProgression] = useState<ProgressionData | null>(null);
  const queryClient = useQueryClient();

  const { mutate: claimCoins, isLoading } = useHttpMutation<GetCoinsResponse>(
    API_URLS.CLAIM_COINS,
    'GET'
  );
  // A few particles for luxury sparkle
  const sparkles = useMemo(() => Array.from({ length: 14 }, (_, i) => i), []);

  useEffect(() => {
    // Fetch updated coins after a longer delay to show animation
    const animationDelay = setTimeout(() => {
      claimCoins(undefined, {
        onSuccess: data => {
          LoggerService.info(`User won coins. New balance: ${data.balance}`);
          setNewBalance(data.balance);
          if (data.xpEarned != null) setXpEarned(data.xpEarned);
          if (data.leveledUp != null) setLeveledUp(data.leveledUp);
          if (data.progression) setProgression(data.progression);
          queryClient.invalidateQueries({
            queryKey: ['currentUserCoins', getStoreState().userReducer.id].join(
              '-'
            ),
          });
          queryClient.invalidateQueries({
            queryKey: ['userProgression', getStoreState().userReducer.id].join('-'),
          });
          setCoinsCollected(true);

          // Complete after showing the collected state
          setTimeout(() => {
            onComplete?.();
          }, 3000);
        },
        onError: err => {
          LoggerService.error('Error fetching coins after win:', err);
          onComplete?.();
        },
      });
    }, 3500);

    return () => clearTimeout(animationDelay);
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        zIndex: 2000,
        backdropFilter: 'blur(6px)',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
      }}
    >
      {/* Soft ambient spotlight */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 50% 35%, rgba(255, 215, 0, 0.14) 0%, rgba(0,0,0,0) 55%)',
          pointerEvents: 'none',
        }}
      />

      {/* Sparkles (luxury feel) */}
      {!coinsCollected &&
        sparkles.map(i => (
          <Box
            key={`sparkle-${i}`}
            sx={{
              position: 'absolute',
              left: `${10 + ((i * 83) % 80)}%`,
              top: `${12 + ((i * 57) % 70)}%`,
              width: `${6 + (i % 4) * 2}px`,
              height: `${6 + (i % 4) * 2}px`,
              borderRadius: '999px',
              background:
                'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.0) 70%)',
              filter: 'blur(0.2px)',
              opacity: 0,
              animation: `sparkleTwinkle 1.8s ease-in-out infinite`,
              animationDelay: `${(i % 6) * 0.15}s`,
              pointerEvents: 'none',
              '@keyframes sparkleTwinkle': {
                '0%': { transform: 'scale(0.6)', opacity: 0 },
                '40%': { transform: 'scale(1.15)', opacity: 0.9 },
                '80%': { transform: 'scale(0.8)', opacity: 0.2 },
                '100%': { transform: 'scale(0.6)', opacity: 0 },
              },
            }}
          />
        ))}

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: '16px', sm: '22px' },
          px: { xs: 3, sm: 4 },
          maxWidth: '100%',
          maxHeight: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* Congratulations Message */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            textAlign: 'center',
            letterSpacing: '-0.02em',
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,236,170,0.95) 35%, rgba(255,215,0,0.95) 65%, rgba(255,255,255,0.95) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'bounce 1.15s cubic-bezier(0.22, 1, 0.36, 1)',
            '@keyframes bounce': {
              '0%': { transform: 'scale(0.5)', opacity: 0 },
              '55%': { transform: 'scale(1.08)' },
              '100%': { transform: 'scale(1)', opacity: 1 },
            },
          }}
        >
          You won the game! 🎉
        </Typography>

        {/* Treasure Stage */}
        <Box
          sx={{
            position: 'relative',
            width: { xs: 'clamp(200px, 85vw, 260px)', sm: '260px' },
            height: { xs: 'clamp(200px, 85vw, 260px)', sm: '260px' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '90vw',
            maxHeight: '90vh',
          }}
        >
          {/* Stage shadow */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '78%',
              height: '22%',
              borderRadius: '999px',
              background:
                'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.16) 55%, rgba(0,0,0,0) 75%)',
              filter: 'blur(4px)',
              pointerEvents: 'none',
            }}
          />

          {/* Luxury Treasure Box */}
          <Box
            sx={{
              position: 'relative',
              width: { xs: 'clamp(150px, 70vw, 190px)', sm: '190px' },
              height: { xs: 'clamp(115px, 60vw, 140px)', sm: '140px' },
              perspective: '1200px',
              filter: 'drop-shadow(0 14px 28px rgba(0, 0, 0, 0.55))',
              zIndex: 10,
            }}
          >
            {/* Back glow */}
            <Box
              sx={{
                position: 'absolute',
                inset: '-18px',
                borderRadius: '24px',
                background:
                  'radial-gradient(circle at 50% 70%, rgba(255,215,0,0.24) 0%, rgba(255,215,0,0.0) 60%)',
                pointerEvents: 'none',
              }}
            />

            {/* Lid (curved, leather-like, gold trim) */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%) rotateX(0deg)',
                transformOrigin: 'center bottom',
                width: '100%',
                height: '62%',
                borderRadius: '18px 18px 14px 14px',
                background:
                  'linear-gradient(135deg, #2b1a12 0%, #6b3b25 35%, #8a5637 55%, #3a2217 100%)',
                border: '2px solid rgba(0,0,0,0.45)',
                boxShadow:
                  'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -10px 16px rgba(0,0,0,0.45)',
                animation: coinsCollected
                  ? 'lidClose 0.9s cubic-bezier(0.22, 1, 0.36, 1) forwards'
                  : 'lidOpen 0.9s cubic-bezier(0.22, 1, 0.36, 1) 1.2s forwards',
                '@keyframes lidOpen': {
                  '0%': { transform: 'translateX(-50%) rotateX(0deg)' },
                  '100%': { transform: 'translateX(-50%) rotateX(-78deg)' },
                },
                '@keyframes lidClose': {
                  '0%': { transform: 'translateX(-50%) rotateX(-78deg)' },
                  '100%': { transform: 'translateX(-50%) rotateX(0deg)' },
                },
              }}
            >
              {/* Lid gold trim */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: '10px 10px auto 10px',
                  height: '10px',
                  borderRadius: '999px',
                  background:
                    'linear-gradient(90deg, #FFD700 0%, #FFF1A8 20%, #FFC400 50%, #FFF1A8 80%, #FFD700 100%)',
                  boxShadow:
                    '0 2px 6px rgba(255, 215, 0, 0.35), inset 0 1px 1px rgba(255,255,255,0.35)',
                  opacity: 0.95,
                  pointerEvents: 'none',
                }}
              />
              {/* Lid highlight sweep */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '14%',
                  left: '12%',
                  width: '42%',
                  height: '26%',
                  borderRadius: '999px',
                  background:
                    'radial-gradient(circle at 35% 35%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.0) 70%)',
                  filter: 'blur(0.6px)',
                  opacity: 0.9,
                  pointerEvents: 'none',
                }}
              />
            </Box>

            {/* Box body (luxury wood + gold frame) */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                height: '56%',
                borderRadius: '14px 14px 18px 18px',
                background:
                  'linear-gradient(135deg, #3a1f12 0%, #a06a3f 25%, #c8945f 45%, #8a5637 65%, #2b1a12 100%)',
                border: '2px solid rgba(0,0,0,0.55)',
                boxShadow:
                  'inset 0 2px 0 rgba(255,255,255,0.16), inset 0 -18px 26px rgba(0,0,0,0.55)',
                animation: coinsCollected
                  ? 'none'
                  : 'treasureShake 0.65s ease-in-out 2s',
                '@keyframes treasureShake': {
                  '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
                  '25%': {
                    transform:
                      'translateX(-50%) translateY(-6px) rotateZ(-1.5deg)',
                  },
                  '75%': {
                    transform:
                      'translateX(-50%) translateY(-6px) rotateZ(1.5deg)',
                  },
                },
              }}
            >
              {/* Wood grain lines */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: '10px 12px 16px 12px',
                  borderRadius: '12px',
                  background:
                    'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.08) 100%)',
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: '10%',
                  right: '10%',
                  top: '40%',
                  height: '2px',
                  background: 'rgba(0,0,0,0.22)',
                  opacity: 0.35,
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: '12%',
                  right: '12%',
                  top: '56%',
                  height: '2px',
                  background: 'rgba(255,255,255,0.12)',
                  opacity: 0.25,
                  pointerEvents: 'none',
                }}
              />

              {/* Gold corner caps */}
              {['tl', 'tr', 'bl', 'br'].map(pos => (
                <Box
                  key={pos}
                  sx={{
                    position: 'absolute',
                    width: '18px',
                    height: '18px',
                    borderRadius: '6px',
                    background:
                      'linear-gradient(135deg, #FFD700 0%, #FFF1A8 30%, #FFC400 60%, #B77A00 100%)',
                    boxShadow:
                      'inset 0 1px 1px rgba(255,255,255,0.35), 0 3px 8px rgba(0,0,0,0.25)',
                    ...(pos === 'tl' ? { top: 6, left: 6 } : {}),
                    ...(pos === 'tr' ? { top: 6, right: 6 } : {}),
                    ...(pos === 'bl' ? { bottom: 6, left: 6 } : {}),
                    ...(pos === 'br' ? { bottom: 6, right: 6 } : {}),
                  }}
                />
              ))}

              {/* Gold bands */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '18px',
                  height: '10px',
                  background:
                    'linear-gradient(90deg, #B77A00 0%, #FFD700 20%, #FFF1A8 50%, #FFD700 80%, #B77A00 100%)',
                  borderTop: '1px solid rgba(255,255,255,0.28)',
                  borderBottom: '1px solid rgba(0,0,0,0.35)',
                  boxShadow: '0 2px 6px rgba(255, 215, 0, 0.25)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: '14px',
                  height: '8px',
                  background:
                    'linear-gradient(90deg, #B77A00 0%, #FFD700 20%, #FFF1A8 50%, #FFD700 80%, #B77A00 100%)',
                  borderTop: '1px solid rgba(255,255,255,0.22)',
                  borderBottom: '1px solid rgba(0,0,0,0.35)',
                  opacity: 0.95,
                }}
              />

              {/* Side metal rings */}
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: '-4px', sm: '-6px' },
                  top: '22px',
                  width: { xs: '8px', sm: '12px' },
                  height: '34px',
                  borderRadius: '10px',
                  background:
                    'linear-gradient(90deg, #6f6f6f 0%, #d8d8d8 45%, #ffffff 55%, #8a8a8a 100%)',
                  boxShadow:
                    'inset 0 1px 2px rgba(255,255,255,0.35), 0 3px 8px rgba(0,0,0,0.25)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  right: { xs: '-4px', sm: '-6px' },
                  top: '22px',
                  width: { xs: '8px', sm: '12px' },
                  height: '34px',
                  borderRadius: '10px',
                  background:
                    'linear-gradient(90deg, #6f6f6f 0%, #d8d8d8 45%, #ffffff 55%, #8a8a8a 100%)',
                  boxShadow:
                    'inset 0 1px 2px rgba(255,255,255,0.35), 0 3px 8px rgba(0,0,0,0.25)',
                }}
              />

              {/* Front ornate lock plate */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '36%',
                  transform: 'translateX(-50%)',
                  width: '52px',
                  height: '44px',
                  borderRadius: '14px',
                  background:
                    'linear-gradient(135deg, #FFD700 0%, #FFF1A8 30%, #FFC400 60%, #B77A00 100%)',
                  boxShadow:
                    'inset 0 1px 2px rgba(255,255,255,0.4), 0 6px 14px rgba(0,0,0,0.35)',
                  border: '1px solid rgba(0,0,0,0.25)',
                }}
              >
                {/* Inner engraving */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: '8px',
                    borderRadius: '10px',
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(0,0,0,0.12) 100%)',
                    border: '1px solid rgba(0,0,0,0.18)',
                  }}
                />
                {/* Keyhole */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '55%',
                    transform: 'translate(-50%, -50%)',
                    width: '12px',
                    height: '16px',
                    borderRadius: '8px',
                    background: 'rgba(0,0,0,0.45)',
                    boxShadow: 'inset 0 2px 3px rgba(255,255,255,0.15)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '38%',
                    transform: 'translate(-50%, -50%)',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.45)',
                  }}
                />
              </Box>
            </Box>

            {/* Coins/shine inside the box when collected */}
            {coinsCollected && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '44%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: { xs: '26px', sm: '30px' },
                  animation: 'float 2.2s ease-in-out infinite',
                  textShadow: '0 0 18px rgba(255,215,0,0.6)',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translate(-50%, -50%)' },
                    '50%': { transform: 'translate(-50%, -56%)' },
                  },
                }}
              >
                ✨
              </Box>
            )}
          </Box>

          {/* Animated coins flying into the box */}
          {!coinsCollected &&
            [0, 1, 2, 3, 4, 5].map(index => (
              <Box
                key={`coin-${index}`}
                sx={{
                  position: 'absolute',
                  width: { xs: '20px', sm: '26px' },
                  height: { xs: '20px', sm: '26px' },
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle at 35% 30%, #FFF3B0 0%, #FFD54F 35%, #FFC107 65%, #FFB300 90%)',
                  border: '2px solid rgba(183, 122, 0, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: '9px', sm: '12px' },
                  fontWeight: 900,
                  boxShadow: '0 2px 10px rgba(255, 215, 0, 0.55)',
                  animation: `coinFly${index} 2s ease-in forwards`,
                  animationDelay: `${0.12 + index * 0.18}s`,
                  '@keyframes coinFly0': {
                    '0%': {
                      transform:
                        'translate(clamp(-60px, -15vw, -80px), -70px) rotate(0deg)',
                      opacity: 1,
                    },
                    '100%': {
                      transform: 'translate(0, 6px) rotate(520deg)',
                      opacity: 0.85,
                    },
                  },
                  '@keyframes coinFly1': {
                    '0%': {
                      transform:
                        'translate(clamp(60px, 15vw, 80px), -70px) rotate(0deg)',
                      opacity: 1,
                    },
                    '100%': {
                      transform: 'translate(0, 6px) rotate(520deg)',
                      opacity: 0.85,
                    },
                  },
                  '@keyframes coinFly2': {
                    '0%': {
                      transform:
                        'translate(clamp(-60px, -15vw, -70px), 70px) rotate(0deg)',
                      opacity: 1,
                    },
                    '100%': {
                      transform: 'translate(0, 6px) rotate(520deg)',
                      opacity: 0.85,
                    },
                  },
                  '@keyframes coinFly3': {
                    '0%': {
                      transform:
                        'translate(clamp(60px, 15vw, 70px), 70px) rotate(0deg)',
                      opacity: 1,
                    },
                    '100%': {
                      transform: 'translate(0, 6px) rotate(520deg)',
                      opacity: 0.85,
                    },
                  },
                  '@keyframes coinFly4': {
                    '0%': {
                      transform: 'translate(0, -95px) rotate(0deg)',
                      opacity: 1,
                    },
                    '100%': {
                      transform: 'translate(0, 6px) rotate(520deg)',
                      opacity: 0.85,
                    },
                  },
                  '@keyframes coinFly5': {
                    '0%': {
                      transform:
                        'translate(clamp(-8px, -2vw, -10px), -105px) rotate(0deg)',
                      opacity: 1,
                    },
                    '100%': {
                      transform: 'translate(0, 6px) rotate(520deg)',
                      opacity: 0.85,
                    },
                  },
                }}
              >
                💰
              </Box>
            ))}

          {/* Rays of light (subtle) */}
          {!coinsCollected && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '999px',
                background:
                  'conic-gradient(from 180deg at 50% 55%, rgba(255,215,0,0.0) 0deg, rgba(255,215,0,0.12) 20deg, rgba(255,215,0,0.0) 40deg, rgba(255,215,0,0.12) 60deg, rgba(255,215,0,0.0) 80deg, rgba(255,215,0,0.12) 100deg, rgba(255,215,0,0.0) 360deg)',
                filter: 'blur(0.2px)',
                opacity: 0.75,
                animation: 'rays 3.6s linear infinite',
                pointerEvents: 'none',
                '@keyframes rays': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          )}
        </Box>

        {/* Coins Collected Message */}
        {coinsCollected && newBalance !== null && (
          <Box
            sx={{
              animation: 'popIn 0.55s cubic-bezier(0.22, 1, 0.36, 1)',
              '@keyframes popIn': {
                '0%': { transform: 'scale(0.6)', opacity: 0 },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 900,
                textAlign: 'center',
                color: '#FFD700',
                textShadow: '0 0 18px rgba(255,215,0,0.35)',
              }}
            >
              💎 Coins Collected!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: 'rgba(255,255,255,0.92)',
              }}
            >
              Total Balance: <strong>{newBalance}</strong> coins
            </Typography>

            {/* XP earned */}
            {xpEarned != null && (
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  color: '#a855f7',
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                ⚡ +{xpEarned} XP earned
              </Typography>
            )}

            {/* Level-up banner */}
            {leveledUp && progression && (
              <Box
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                  boxShadow: '0 0 20px rgba(168,85,247,0.5)',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 900, color: '#fff' }}>
                  🎉 Level Up! You are now {progression.levelEmoji} {progression.levelName}
                </Typography>
              </Box>
            )}

            {/* Progress to next level (when not leveled up, show remaining) */}
            {!leveledUp && progression && progression.nextLevelMinXP !== -1 && (
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}
              >
                {progression.nextLevelMinXP - progression.xp} XP to next level
              </Typography>
            )}
          </Box>
        )}

        {isLoading && (
          <Typography
            variant="body2"
            sx={{ marginTop: '12px', color: 'rgba(255,255,255,0.85)' }}
          >
            Calculating rewards...
          </Typography>
        )}
      </Box>
    </Box>
  );
};
